import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { FacebookService } from 'src/app/services/facebook.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

// Facebook 用戶接口
interface FacebookUser {
  id: string;
  name: string;
  email: string;
  picture: string;
}

// Facebook 頁面接口
interface FacebookPage {
  id: string;
  name: string;
  access_token: string;
  category: string;
  picture?: {
    data?: {
      url?: string;
    };
  };
  fan_count?: number;
  about?: string;
  website?: string;
  phone?: string;
  emails?: string[];
  location?: any;
  instagram_business_account?: {
    id: string;
    name: string;
    username: string;
    profile_picture_url: string;
  };
}

interface InstagramPage {
  id: string;
  fansPageName: string;
  fansPageId: string;
  name: string;
  username: string;
  fansPage_access_token: string;
  profile_picture_url?: string;
}

declare const FB: any;

@Component({
  selector: 'app-facebook-callback',
  templateUrl: './facebook-callback.component.html',
  styleUrls: ['./facebook-callback.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSpinModule,
    NzButtonModule,
    NzAvatarModule,
    NzIconModule,
    NzTableModule,
    NzRadioModule,
    TestIdDirective,
    I18nPipe
  ]
})
export class FacebookCallbackComponent implements OnInit {
  private readonly facebookService = inject(FacebookService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  loading = true;
  error: string | null = null;
  facebookUser: FacebookUser | null = null;
  facebookPages: FacebookPage[] = [];
  selectedPageId: string | null = null;
  channelType: string | null = null;
  accessToken: string | null = null;
  processingStatus: 'loading' | 'user_loaded' | 'pages_loaded' | 'error' = 'loading';
  pagesLoading = false;
  instagramPages: InstagramPage[] = [];

  private readonly serviceNumberId: string | null = null;
  private bindLoading = false;
  private messageSent = false;

  constructor(
    private readonly message: NzMessageService,
    private readonly cdr: ChangeDetectorRef
  ) {
    // 從 localStorage 獲取 serviceNumberId
    this.serviceNumberId = localStorage.getItem('fb_binding_service_number_id');
    this.channelType = localStorage.getItem('fb_binding_service_channel');

    // 如果沒有找到 serviceNumberId，設置錯誤狀態
    if (!this.serviceNumberId) {
      this.processingStatus = 'error';
      this.error = this.i18n.fanyi('tenant.channel.callback.service-number-not-found');
      this.loading = false;
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.handleFacebookCallback();
  }

  private handleFacebookCallback(): void {
    const urlParams = this.parseUrlParameters();

    if (urlParams.error) {
      this.handleError(urlParams.error, urlParams.error_description);
      return;
    }

    if (urlParams.access_token) {
      this.accessToken = urlParams.access_token;
      console.log('Access token received successfully');

      // 獲取 Facebook 用戶信息
      if (this.accessToken) {
        this.getFacebookUserInfo(this.accessToken);
      }
    } else {
      this.handleError('NO_ACCESS_TOKEN', this.i18n.fanyi('tenant.channel.callback.no-access-token'));
    }
  }

  private parseUrlParameters(): any {
    const params: any = {};

    // 解析 hash 參數
    const hash = window.location.hash.substring(1);
    if (hash) {
      const hashParams = new URLSearchParams(hash);
      hashParams.forEach((value, key) => {
        params[key] = value;
      });
    }

    // 解析 search 參數
    const search = window.location.search.substring(1);
    if (search) {
      const searchParams = new URLSearchParams(search);
      searchParams.forEach((value, key) => {
        params[key] = value;
      });
    }

    // 處理特殊格式 ?#access_token=xxx
    const fullUrl = window.location.href;
    const specialMatch = fullUrl.match(/\?#(.+)/);
    if (specialMatch) {
      const specialParams = new URLSearchParams(specialMatch[1]);
      specialParams.forEach((value, key) => {
        params[key] = value;
      });
    }

    console.log('Parsed URL parameters:', params);
    return params;
  }
  private async getFacebookUserInfo(accessToken: string): Promise<void> {
    try {
      await this.facebookService.loadFacebookSDK(sessionStorage.getItem('facebook_app_id') ?? '');
      this.facebookUser = await this.facebookService.fetchUserInfo(accessToken);
      this.processingStatus = 'user_loaded';

      // 自動獲取頁面列表
      if (this.channelType === 'Facebook') {
        await this.getFacebookPages();
      } else if (this.channelType === 'Instagram') {
        await this.getInstagramPages();
      }
    } catch (error) {
      this.handleError('USER_INFO_ERROR', this.i18n.fanyi('tenant.channel.callback.facebook-user-info-failed'));
    }
  }

  private async getFacebookPages(): Promise<void> {
    if (!this.accessToken) return;

    this.pagesLoading = true;
    try {
      this.facebookPages = await this.facebookService.getFacebookPages(this.accessToken);
      this.processingStatus = 'pages_loaded';
      this.loading = false;

      if (this.facebookPages.length === 0) {
        this.message.warning(this.i18n.fanyi('tenant.channel.callback.no-facebook-pages'));
      }
    } catch (error) {
      this.handleError('PAGES_LOAD_ERROR', this.i18n.fanyi('tenant.channel.callback.facebook-pages-load-failed'), error);
    } finally {
      this.pagesLoading = false;
    }
  }

  private async getInstagramPages(): Promise<void> {
    if (!this.accessToken) return;

    this.pagesLoading = true;
    try {
      this.instagramPages = await this.facebookService.getInstagramPages(this.accessToken);
      this.processingStatus = 'pages_loaded';
      this.loading = false;
    } catch (error) {
      this.handleError('INSTAGRAM_LOAD_ERROR', this.i18n.fanyi('tenant.channel.callback.instagram-pages-load-failed'));
    } finally {
      this.pagesLoading = false;
      this.cdr.detectChanges();
    }
  }

  // 確認選擇並綁定
  confirmSelection(): void {
    if (!this.selectedPageId || this.bindLoading || !this.serviceNumberId) {
      return;
    }

    const selectedPage =
      this.channelType === 'Facebook'
        ? this.facebookPages.find(page => page.id === this.selectedPageId)
        : this.instagramPages.find(page => page.id === this.selectedPageId);

    if (!selectedPage) {
      this.message.error(this.i18n.fanyi('tenant.channel.callback.select-page-required'));
      return;
    }

    this.bindLoading = true;

    // 發送選中的頁面數據到父窗口
    this.sendMessageToParent({
      type: 'PAGE_SELECTED',
      pageData: selectedPage,
      channelType: this.channelType,
      serviceNumberId: this.serviceNumberId
    });

    // 關閉窗口
    setTimeout(() => {
      this.closeWindow();
    }, 1000);
  }

  // 修改錯誤處理，發送錯誤消息到父窗口
  private handleError(errorType: string, errorDescription?: string, originalError?: unknown): void {
    this.error = errorDescription ?? errorType;
    this.processingStatus = 'error';
    this.loading = false;
    const errorDetail = this.extractErrorDetail(originalError);

    console.error('Facebook login error:', errorType, errorDescription, originalError);
    this.message.error(`${this.i18n.fanyi('tenant.channel.callback.facebook-login-failed')}: ${this.error}`);

    this.sendMessageToParent({
      type: 'BIND_ERROR',
      message: this.error,
      errorType,
      errorDetail
    });
  }

  private extractErrorDetail(error: unknown): string | undefined {
    if (!error) {
      return undefined;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  // 修改取消操作
  closeWindow(): void {
    // 發送取消消息到父窗口
    this.sendMessageToParent({
      type: 'BIND_CANCELLED'
    });

    localStorage.removeItem('fb_binding_service_number_id');
    localStorage.removeItem('fb_binding_service_channel');
    if (window.opener) {
      window.close();
    } else {
      window.location.href = '/';
    }
  }
  private sendMessageToParent(message: any): void {
    // 防护：防止重复发送消息
    if (this.messageSent) {
      console.log('Message already sent, ignoring duplicate send attempt');
      return;
    }

    if (window.opener && !window.opener.closed) {
      try {
        // 從 sessionStorage 獲取父窗口源
        const targetOrigin = sessionStorage.getItem('parent_origin') || window.location.origin;

        this.messageSent = true;
        window.opener.postMessage(message, targetOrigin);
        console.log('Message sent to parent window, type:', message?.type, 'Target origin:', targetOrigin);

        // 发送消息后延迟关闭窗口，确保消息被接收
        setTimeout(() => {
          window.close();
        }, 500);
      } catch (error) {
        console.error('Failed to send message to parent window:', error);
      }
    } else {
      console.warn('Parent window not available or closed');
    }
  }
}
